import {
  deserify,
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
  QuestionContainerWithSolution,
  theme,
  ToggleSwitch,
} from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import { SessionStatusEnum } from '@protos/learning_management/lms.db_pb';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLiveQuizContext } from '../../app/Context/LiveQuizContextProvider';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { HOMEWORK_ONGOING } from '../../routeHandling/RoutesNomenclature';
import { IconDataWithLabel } from '../../components/IconDataWithLabel';
import { LiveQuizIndicatorBar } from './components/LiveQuizIndicatorBar';
import { LiveQuizResponseList } from './components/LiveQuizResponseList';
import { usePauseLiveQuizOnLeave } from './hooks/usePauseLiveQuizOnLeave';
import { LiveQuizStalePopup } from './components/LiveQuizStalePopup';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(0), md: pxToRem(20) },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    gap: { xs: pxToRem(0), md: pxTovW(30) },
  },
  lastQuestionResults: {
    mt: { xs: pxToRem(20), md: pxTovW(20) },
    p: { xs: pxToRem(14), md: pxTovW(20) },
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    backgroundColor: '#FFFFFF',
  },
  lastQuestionStatsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: { xs: pxToRem(14), md: pxTovW(14) },
  },
};

// Same height as ToggleSwitch (the tallest thing in the left "View Responses"
// header row) — giving the right column's "Question X of Y" label the same
// row height/margin means both columns' bordered cards below start at the
// same y, instead of the question card sitting noticeably higher than the
// response list (the toggle row is taller than a single line of text).
const HEADER_ROW_HEIGHT = { xs: pxToRem(46), md: pxTovW(55), lg: pxTovW(36) };
const HEADER_ROW_MARGIN_BOTTOM = { xs: pxToRem(16), md: pxTovW(16) };

// Question-by-question control, laid out to match ClickerAssessment's live
// play screen (indicator bar on top, response list left / question+controls
// right) rather than a bare debug page — same building blocks
// (LiveQuizIndicatorBar/LiveQuizResponseList mirror
// ConnectedClassIndicator/ResponseList's structure), so this reads as the
// same kind of screen as the hardware flow, not a different product. The
// teacher explicitly ends the current question (triggering the reveal for
// students) before advancing — matching the teacher-controlled-reveal
// decision and the "no server timer" scope. Question content (text/options/
// correct answer) was fetched once at createLiveQuizSession time into
// `questions` — this screen only ever picks an already-cached item out of
// that array by questionId, never refetches.
export const LiveQuizPlay = () => {
  usePauseLiveQuizOnLeave();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    startLiveQuizQuestion,
    endLiveQuizQuestion,
    endLiveQuizSession,
    resetLiveQuizData,
  } = useLiveQuizContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const {
    live_quiz_session,
    question_sessions,
    questions,
    joined_participants,
    roster,
    current_question_status,
    current_question_responses,
    last_question_stats,
  } = deserify(useAppSelector((state) => state.liveQuiz));
  const location = useLocation();
  const autoStartQuestionSessionId = (
    location.state as { autoStartQuestionSessionId?: bigint }
  )?.autoStartQuestionSessionId;
  // Derived once at mount rather than always starting at 0 — landing here
  // straight from a resumed (previously paused) session, or a plain page
  // refresh, both leave this as the only signal for which question was
  // actually in progress; live_quiz_session.currentQuestionSessionId is the
  // authoritative pointer either way.
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (autoStartQuestionSessionId) {
      const targetIdx = question_sessions.findIndex(
        (qs) => qs.liveQuizQuestionSessionId === autoStartQuestionSessionId
      );
      // seed one behind the target — handleNextQuestion's existing
      // currentIndex + 1 logic lands exactly on it
      if (targetIdx > 0) return targetIdx - 1;
    }
    if (live_quiz_session?.currentQuestionSessionId) {
      const idx = question_sessions.findIndex(
        (qs) =>
          qs.liveQuizQuestionSessionId ===
          live_quiz_session.currentQuestionSessionId
      );
      if (idx >= 0) return idx;
    }
    // Nothing currently live — either this session hasn't started its
    // first question yet, or its last question was already ended/revealed
    // but the teacher navigated away before clicking "End Quiz" (Play's own
    // pause-on-leave paused the session rather than ending it). Resuming
    // into that second case previously always reset to question 1, even
    // though it was really sitting on the last question waiting for "End
    // Quiz" — land on the last completed one instead, so its reveal and
    // the End Quiz button are still there, not a re-opened question 1.

    //same logic is there in LiveQuizStartTrigger before starting the quiz
    for (let i = question_sessions.length - 1; i >= 0; i--) {
      if (
        question_sessions[i].sessionStatus ===
        SessionStatusEnum.SESSION_STATUS_COMPLETED
      ) {
        return i;
      }
    }
    return 0;
  });
  const [showResponse, setShowResponse] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  const currentQuestion = question_sessions[currentIndex];
  const currentQuestionContent = questions.find(
    (q) => q.questionId === currentQuestion?.questionId
  );
  const isLastQuestion = currentIndex === question_sessions.length - 1;
  const isQuestionLive = current_question_status === 'SESSION_STATUS_STARTED';
  const answeredCount = Object.keys(current_question_responses).length;
  const correctCount = isQuestionLive
    ? undefined
    : Object.values(current_question_responses).filter((r) => r.isCorrect)
        .length;

  const handleEndQuestion = async () => {
    if (!live_quiz_session || !user_info?.teacherProfileId || !currentQuestion)
      return;
    setAdvancing(true);
    try {
      await endLiveQuizQuestion(
        live_quiz_session.liveQuizSessionId,
        currentQuestion.liveQuizQuestionSessionId,
        user_info.teacherProfileId
      );
    } finally {
      setAdvancing(false);
    }
  };

  const handleNextQuestion = async () => {
    if (!live_quiz_session || !user_info?.teacherProfileId) return;
    const nextIndex = currentIndex + 1;
    const nextQuestion = question_sessions[nextIndex];
    if (!nextQuestion) return;
    setAdvancing(true);
    try {
      const res = await startLiveQuizQuestion(
        live_quiz_session.liveQuizSessionId,
        nextQuestion.liveQuizQuestionSessionId,
        user_info.teacherProfileId
      );
      if (res) setCurrentIndex(nextIndex);
    } finally {
      setAdvancing(false);
    }
  };

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartQuestionSessionId && !autoStartedRef.current) {
      autoStartedRef.current = true;
      handleNextQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleEndQuiz = async () => {
    if (!live_quiz_session || !user_info?.teacherProfileId) return;
    setAdvancing(true);
    try {
      const ok = await endLiveQuizSession(
        live_quiz_session.liveQuizSessionId,
        user_info.teacherProfileId
      );
      if (ok) {
        navigate(HOMEWORK_ONGOING);
        resetLiveQuizData(); // navigate first so Play doesn't flash "Question 1 of 0"
      }
    } finally {
      setAdvancing(false);
    }
  };
  const joinedIds = useMemo(
    () =>
      new Set(
        joined_participants
          .filter((p) => p.isOnline)
          .map((p) => String(p.studentId))
      ),
    [joined_participants]
  );

  const totalJoined = joinedIds.size;
  return (
    <Box>
      <LiveQuizIndicatorBar
        totalJoined={totalJoined}
        totalStudents={roster?.length ?? joined_participants.length}
        totalAnswered={answeredCount}
        correct={correctCount}
        averageTime={last_question_stats?.averageTimeInSec}
        averagemarks={last_question_stats?.averageScorePercent}
      />

      <Box sx={{ ...styles.root, paddingTop: pxToRem(20) }}>
        <Box
          sx={{
            width: { xs: '95%', md: pxTovW(472) },
            order: { xs: '2', md: '1' },
            margin: 'auto',
            mt: { xs: pxToRem(10), md: 'unset' },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: HEADER_ROW_HEIGHT,
              marginTop: { md: pxTovW(5) },
              marginBottom: HEADER_ROW_MARGIN_BOTTOM,
              background: 'white',
              p: { xs: pxToRem(5), md: 'unset' },
              boxSizing: 'border-box',
            }}
          >
            <Typography variant={isMobile ? 'h2' : 'h2'} fontWeight="medium">
              View Responses
            </Typography>
            <ToggleSwitch
              checked={showResponse}
              disabled={isQuestionLive}
              handleChange={() => setShowResponse(!showResponse)}
            />
          </Box>

          <LiveQuizResponseList
            showResponse={showResponse}
            participants={joined_participants}
            responses={current_question_responses}
          />
        </Box>

        <Box
          sx={{
            width: { xs: '95%', md: 'unset' },
            flexGrow: 1,
            order: { xs: '1', md: '2' },
            display: 'flex',
            flexDirection: 'column',
            marginX: 'auto',
          }}
        >
          <Typography
            variant="h4"
            color="primary.main"
            textAlign="center"
            sx={{ mb: 1 }}
          >
            Question {currentIndex + 1} of {question_sessions.length}
          </Typography>

          {currentQuestionContent && (
            <QuestionContainerWithSolution
              questionTypeDisplay
              questionNumber={currentIndex + 1}
              question={new Question(currentQuestionContent)}
              homeworkAttempt
              disableInput
              imageResizeEnabled
              // This screen's own `questions` array was fetched un-stripped
              // at session start (the teacher was always allowed to see the
              // correct answer) — only gating *when* it's shown, so it
              // appears at the same "End Question & Reveal" moment students
              // see it, not before. Deliberately not `!isQuestionLive` —
              // that's also true before the first question has ever been
              // started (current_question_status is still undefined then).
              showAnswer={
                current_question_status === 'SESSION_STATUS_COMPLETED'
              }
              defaultExpandedAccordion={
                current_question_status === 'SESSION_STATUS_COMPLETED'
              }
            />
          )}
          <Box
            sx={{ display: 'flex', justifyContent: 'center', mt: pxToRem(20) }}
          >
            {isQuestionLive ? (
              <PrimaryButton
                onClick={handleEndQuestion}
                disabled={advancing}
                sx={{
                  width: { xs: pxToRem(220), md: pxTovW(320) },
                  height: { xs: pxToRem(45), md: pxTovW(60) },
                }}
              >
                <Typography
                  variant="bodyText"
                  color="common.white"
                  fontWeight="bold"
                >
                  {advancing ? 'Ending…' : 'End Question & Reveal'}
                </Typography>
              </PrimaryButton>
            ) : isLastQuestion ? (
              <PrimaryButton
                onClick={handleEndQuiz}
                disabled={advancing}
                sx={{
                  width: { xs: pxToRem(220), md: pxTovW(320) },
                  height: { xs: pxToRem(45), md: pxTovW(60) },
                }}
              >
                <Typography
                  variant="bodyText"
                  color="common.white"
                  fontWeight="bold"
                >
                  {advancing ? 'Ending…' : 'End Quiz'}
                </Typography>
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={handleNextQuestion}
                disabled={advancing}
                sx={{
                  width: { xs: pxToRem(220), md: pxTovW(320) },
                  height: { xs: pxToRem(45), md: pxTovW(60) },
                }}
              >
                <Typography
                  variant="bodyText"
                  color="common.white"
                  fontWeight="bold"
                >
                  {advancing ? 'Starting…' : 'Next Question'}
                </Typography>
              </PrimaryButton>
            )}
          </Box>
          {!isQuestionLive && last_question_stats && (
            <Box sx={styles.lastQuestionResults}>
              <Typography
                variant="h4"
                fontWeight="medium"
                textAlign="center"
                sx={{ mb: pxToRem(14) }}
              >
                Last Question Results
              </Typography>
              <Box sx={styles.lastQuestionStatsRow}>
                <IconDataWithLabel
                  iconName="answered"
                  data={last_question_stats.answeredCount.toString()}
                  label="Answered"
                />
                <IconDataWithLabel
                  iconName="correct"
                  data={last_question_stats.correctCount.toString()}
                  label="Correct"
                />
                <IconDataWithLabel
                  iconName="wrong"
                  data={last_question_stats.incorrectCount.toString()}
                  label="Incorrect"
                />
                <IconDataWithLabel
                  iconName="pending"
                  data={last_question_stats.unansweredCount.toString()}
                  label="Unanswered"
                  hideDivider
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <LiveQuizStalePopup />
    </Box>
  );
};

export default LiveQuizPlay;
